import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AridosSurveySuccessModalComponent } from 'src/app/profiles/aridos-mixer/despacho/firma/survey-success-modal/survey-success-modal.component';
import { AlertService } from 'src/app/services/auth';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { Order } from 'src/app/shared/models/orders';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { SyncService } from 'src/app/services/offline/sync.service';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { CorrelationContextService } from 'src/core/logging/correlation-context.service';
import { EVENT_LOG_ENDPOINTS } from 'src/core/logging/event-log-endpoints';
import {
  httpContextFromUnknown,
  httpSuccessContext,
  mergeHttpLogPayload,
} from 'src/core/logging/http-event-log-context';
import { VIAJES_FIRMA_EMAIL_PATTERN } from 'src/app/services/pedidos/viajes-firma.mapper';
import { firmaSendErrorMessage } from 'src/app/services/firma-digital/viajes-firma-errors';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-pedidos-pendiente-firma',
    templateUrl: './pedidos-pendiente-firma.component.html',
    styleUrls: ['./pedidos-pendiente-firma.component.css'],
    standalone: false
})
export class PedidosPendienteFirmaComponent implements OnInit {
  @Input() order: Order;
  @Input() embedded = false;
  @ViewChild('contenidoModal') contenidoModal!: TemplateRef<any>;
  @ViewChild('modalContentSurveySuccess') modalContentSurveySuccess!: TemplateRef<any>;

  dialogRef: MatDialogRef<any>;
  isChecked = false;
  emailPattern = VIAJES_FIRMA_EMAIL_PATTERN.source;
  data: any = {
    name: '',
    rut: '',
    signature: '',
    mail: '',
    mailDos: '',
    dvrut: '',
  };
  savedOffline = false;
  serverConfirmed = false;
  submitting = false;
  alreadySigned = false;
  surveyVisible = false;

  constructor(
    private dialog: MatDialog,
    private orderService: OrderService,
    private scrollStrategyOptions: ScrollStrategyOptions,
    private offlineStorage: OfflineStorageService,
    private syncService: SyncService,
    private alertService: AlertService,
    private router: Router,
    private eventLog: EventLogService,
    private correlationContext: CorrelationContextService
  ) {}

  showModalSurveySuccess(res: any) {
    this.dialog.open(AridosSurveySuccessModalComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: '420px',
      data: res,
    });
  }

  ngOnInit(): void {
    this.alreadySigned = !!this.order?.isSignature;
  }

  onSignatureChange(value: string): void {
    this.data.signature = value;
  }

  onOpenModal() {
    this.dialogRef = this.dialog.open(this.contenidoModal, {
      maxWidth: '100%',
      scrollStrategy: this.scrollStrategyOptions.noop(),
      disableClose: true,
    });
  }

  onCierrenModal() {
    this.dialogRef.close();
  }

  saveData() {
    if (this.isDisabled() || this.submitting || this.alreadySigned) return;

    this.submitting = true;
    const rutCompleto = `${this.data.rut}${this.data.dvrut.toUpperCase()}`;
    const tkt = this.order.tkt.toString();
    const correlationId = this.correlationContext.startFlow();
    this.eventLog.log('firma-digital', 'submit_start', { tkt, source: 'pedidos', correlationId });

    this.orderService
      .setSignedGuideCanvas(
        tkt,
        this.order.orderNumber.toString(),
        rutCompleto,
        this.data.name,
        this.data.signature,
        this.data.mail,
        this.alreadySigned,
        undefined,
        undefined,
        this.data.mailDos
      )
      .pipe(take(1))
      .subscribe({
        next: async (submitResult) => {
          if (submitResult.status === 'sent') {
            this.savedOffline = false;
            this.serverConfirmed = true;
            this.eventLog.log(
              'firma-digital',
              'submit_confirmed',
              mergeHttpLogPayload(
                { tkt, correlationId },
                httpSuccessContext(EVENT_LOG_ENDPOINTS.VIAJES_FIRMA)
              )
            );
            this.correlationContext.endFlow();
            this.onOpenModal();
            this.submitting = false;
            return;
          }

          if (!this.offlineStorage.isOnline()) {
            this.savedOffline = true;
            this.serverConfirmed = false;
            this.eventLog.log('firma-digital', 'submit_offline', { tkt, correlationId });
            this.correlationContext.endFlow();
            this.onOpenModal();
            this.submitting = false;
            return;
          }

          const result = await this.syncService.waitForOpSent(tkt, 'signedCanvas');
          if (result === 'sent') {
            this.savedOffline = false;
            this.serverConfirmed = true;
            this.eventLog.log(
              'firma-digital',
              'submit_confirmed',
              mergeHttpLogPayload(
                { tkt, correlationId },
                httpSuccessContext(EVENT_LOG_ENDPOINTS.VIAJES_FIRMA)
              )
            );
            this.correlationContext.endFlow();
            this.onOpenModal();
          } else {
            this.alertService.error(this.firmaFailureMessage(tkt));
            this.eventLog.log(
              'firma-digital',
              'error',
              mergeHttpLogPayload(
                { action: 'sync_wait', tkt, correlationId },
                {
                  endpointOrigen: EVENT_LOG_ENDPOINTS.VIAJES_FIRMA,
                  codigoError: 'SYNC_WAIT_FAILED',
                }
              )
            );
            this.correlationContext.endFlow();
          }
          this.submitting = false;
        },
        error: (err) => {
          this.submitting = false;
          this.alertService.error(firmaSendErrorMessage(err));
          this.eventLog.log(
            'firma-digital',
            'error',
            mergeHttpLogPayload(
              { action: 'submit', tkt, correlationId },
              httpContextFromUnknown(err, EVENT_LOG_ENDPOINTS.VIAJES_FIRMA)
            )
          );
          this.correlationContext.endFlow();
        },
      });
  }

  private firmaFailureMessage(tkt: string): string {
    const failure = this.syncService.consumeLastOpFailure(tkt, 'signedCanvas');
    if (failure?.terminal) {
      return failure.message;
    }
    return 'No se pudo enviar la firma. Quedó guardada y se reintentará.';
  }

  goBack() {
    this.dialogRef.close();
    this.router.navigate(['/pedidosEntregados']);
  }

  goBackToPedidosPendientes() {
    this.dialogRef.close();
    this.router.navigate(['/pedidospendiente']);
  }

  isDisabled() {
    const hasTwoConsecutiveKs = /[kK]{2}/.test(this.data.rut + this.data.dvrut);
    const isEmailInvalid =
      (this.data.mail && !VIAJES_FIRMA_EMAIL_PATTERN.test(this.data.mail)) ||
      (this.data.mailDos && !VIAJES_FIRMA_EMAIL_PATTERN.test(this.data.mailDos));
    return (
      this.alreadySigned ||
      this.submitting ||
      this.data.name === '' ||
      this.data.rut === '' ||
      this.data.dvrut === '' ||
      !(
        this.data.rut.length + this.data.dvrut.length === 8 ||
        this.data.rut.length + this.data.dvrut.length === 9
      ) ||
      hasTwoConsecutiveKs ||
      this.data.signature === '' ||
      isEmailInvalid
    );
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'k', 'K', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab',
    ];
    const inputValue = (event.target as HTMLInputElement).value;

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }

    if (
      inputValue.length >= 8 &&
      !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(event.key)
    ) {
      event.preventDefault();
    }
  }

  onDvRutChange(value: string): void {
    this.data.dvrut = (value ?? '')
      .replace(/[^0-9kK]/g, '')
      .slice(0, 1)
      .toUpperCase();
  }
}

